import React, { useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';

import { useAccountStore, useManagementStore, useSettingStore, useUserState } from '../../store';
import axios from '../../utilities/axios';
import { FORMAT, LOGS } from '../../utilities/functions';

import LeftPanel from './components/LeftPanel';
import RightPanel from './components/RightPanel';
import TradeReviewLayout from './components/TradeReviewLayout';

import './styles.scss';

export default function TradeIndividualPage() {
    // State declarations
    const [profitTargetValue, setProfitTarget] = useState('');
    const [initialTarget, setInitialTarget] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [trade, setTrade] = useState({});
    const [tradeRisk, setTradeRisk] = useState('');
    const [plannedR, setPlannedR] = useState('');
    const [timezone, setTimeZone] = useState();
    const [realizedR, setRealizedR] = useState('');
    const [stopLossValue, setStopLoss] = useState('');
    const [tradeNotes, setTradeNotes] = useState(null);
    const [previousTrade, setPreviousTrade] = useState(null);
    const [nextTrade, setNextTrade] = useState(null);
    const [isCalculatingExcursions, setIsCalculatingExcursions] = useState(false);

    // Hooks
    const { setMarks } = useSettingStore((state) => state);
    const { tradeId } = useParams();
    const { getUser } = useUserState();
    const { viewAs } = useManagementStore((state) => state);
    const user = getUser();
    const { setAllAccounts, allAccounts } = useAccountStore((state) => state);
    const navigate = useNavigate();

    const [isViewAsActive, setViewAsActive] = useState(
        JSON.stringify(viewAs?.newUser?.token || null) !==
            JSON.stringify(viewAs?.mainUser?.token || null)
    );

    // Fetch accounts
    const fetchAccounts = () =>
        axios
            .get('crud/account', {
                headers: {
                    token: isViewAsActive ? viewAs.newUser.token : user?.token
                }
            })
            .then(({ data }) => data.map((a) => FORMAT.ACCOUNTS_BEAUTIFY(a)));

    const { isLoading: isAccountLoading } = useQuery(
        ['accounts', viewAs],
        () =>
            fetchAccounts().then(async (accounts) => {
                const results = await LOGS.processLogs(accounts, setAllAccounts);
                return results;
            }),
        { enabled: user.token !== undefined }
    );

    // API handlers
    const addProfitTargetToTrade = (profitTarget) => {
        axios.put(`crud/trades/profittarget/${tradeId}`, { profitTarget }).then(({ data }) => {
            const beautifulData = FORMAT.TRADE_BEAUTIFY(data, timezone);
            setProfitTarget(profitTarget);
            setPlannedR(beautifulData.plannedRMultiple);
            setRealizedR(beautifulData.realizedRMultiple);
            setInitialTarget(beautifulData.initialTarget);
        });
    };

    const addStopLossToTrade = (stopLoss) => {
        axios.put(`crud/trades/stoploss/${tradeId}`, { stopLoss }).then(({ data }) => {
            const beautifulData = FORMAT.TRADE_BEAUTIFY(data, timezone);
            setStopLoss(stopLoss);
            setPlannedR(beautifulData.plannedRMultiple);
            setRealizedR(beautifulData.realizedRMultiple);
            setTradeRisk(beautifulData.tradeRisk);
            // Update trade with excursions if calculated
            setTrade(beautifulData);
        });
    };

    const calculateExcursions = () => {
        setIsCalculatingExcursions(true);
        axios
            .post('trades/calculate-excursions', { tradeIds: [tradeId] })
            .then(() => axios.get(`crud/trades/${tradeId}`))
            .then(({ data }) => {
                const beautifulData = FORMAT.TRADE_BEAUTIFY(data, timezone);
                setTrade(beautifulData);
                setIsCalculatingExcursions(false);
            })
            .catch((error) => {
                console.error('Error calculating excursions:', error);
                setIsCalculatingExcursions(false);
            });
    };

    // Effects
    useEffect(() => {
        setIsLoading(true);
        axios
            .get(`crud/trades/${tradeId}`)
            .then(({ data }) => {
                const { brokerTimezone } = allAccounts.find((a) => a.key === data.accountRef._id);
                if (
                    user.role === 'admin' ||
                    viewAs?.newUser?.id === data.accountRef._id ||
                    viewAs?.mainUser?.id === data.accountRef._id
                ) {
                    setViewAsActive(false);
                }
                setTimeZone(() => brokerTimezone);
                setTrade(FORMAT.TRADE_BEAUTIFY(data, brokerTimezone));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [tradeId]);

    useEffect(() => {
        // Preparing TradingView Marks. This works with either createShape method and createExecutionShape
        if (Object.keys(trade).length > 0) {
            // We don't want to show trades for Option trades
            if (trade.executions && trade.executions.length > 0 && trade.assetType !== 'Option') {
                const marks = trade.executions.map((e) => e.mark);
                setMarks(marks);
            }
        }

        setPreviousTrade(trade.previousTrade || null);
        setNextTrade(trade.nextTrade || null);

        if (trade.profit_target) setProfitTarget(trade.profit_target);
        if (trade.stop_loss) setStopLoss(trade.stop_loss);
        if (trade.initialTarget) setInitialTarget(trade.initialTarget);
        if (trade.tradeRisk) setTradeRisk(trade.tradeRisk);
        if (trade.plannedRMultiple) setPlannedR(trade.plannedRMultiple);
        if (trade.realizedRMultiple) setRealizedR(trade.realizedRMultiple);
        if (trade.notes) setTradeNotes(trade.notes);
    }, [setMarks, trade]);

    // Loading state
    if (isLoading || isAccountLoading) {
        return (
            <div className="trade-review-loading">
                <Spin size="large" />
            </div>
        );
    }

    // Navigation header component
    const navigationHeader = (
        <>
            <Button
                type="default"
                onClick={() => navigate(`/trades/${previousTrade}`)}
                disabled={!previousTrade}
                icon={<LeftOutlined />}
                style={{ fontWeight: 600 }}
            >
                Previous trade
            </Button>
            <Button
                type="default"
                onClick={() => navigate(`/trades/${nextTrade}`)}
                disabled={!nextTrade}
                style={{ fontWeight: 600 }}
            >
                Next trade <RightOutlined />
            </Button>
        </>
    );

    return (
        <TradeReviewLayout
            navigationHeader={navigationHeader}
            leftPanel={
                <LeftPanel
                    trade={trade}
                    tradeId={trade._id}
                    profitTargetValue={profitTargetValue}
                    stopLossValue={stopLossValue}
                    initialTarget={initialTarget}
                    tradeRisk={tradeRisk}
                    plannedR={plannedR}
                    realizedR={realizedR}
                    onProfitTargetChange={addProfitTargetToTrade}
                    onStopLossChange={addStopLossToTrade}
                    onCalculateExcursions={calculateExcursions}
                    isCalculatingExcursions={isCalculatingExcursions}
                    isViewAsActive={isViewAsActive}
                    token={isViewAsActive ? viewAs.newUser.token : user?.token}
                    timezone={timezone}
                />
            }
            rightPanel={(isLeftPanelCollapsed, toggleLeftPanel) => (
                <RightPanel
                    trade={trade}
                    tradeId={trade._id}
                    tradeNotes={tradeNotes}
                    setTradeNotes={setTradeNotes}
                    isViewAsActive={isViewAsActive}
                    timezone={timezone}
                    isLeftPanelCollapsed={isLeftPanelCollapsed}
                    onToggleLeftPanel={toggleLeftPanel}
                />
            )}
        />
    );
}
